import { supabase } from "@/integrations/supabase/client";
import type {
  PhoneNumberWithDetails,
  PhoneNumberFilters,
  PhoneNumberStats,
  PhoneNumberListResponse,
} from "@/types/phone-numbers";

interface RawPhoneNumberData {
  id: number;
  phone_number: string;
  friendly_name: string | null;
  status: string;
  date_created: string;
  country_code: string | null;
  is_transfer: boolean | null;
  metadata: Record<string, any> | null;
  agent_id: string;
  agents: {
    id: string;
    name: string;
    user_id: string | null;
  } | null;
}

const ACTIVE_PORT_STATUSES = new Set(["active", "completed", "canceled", "failed"]);

const isPortingTask = (item: { status: string; metadata?: Record<string, any> | null }) => {
  if (item.metadata?.source !== "ported") return false;
  const portStatus = item.metadata?.port_status || item.status;
  return item.status === "port_requested" || item.status === "port_pending" || !ACTIVE_PORT_STATUSES.has(portStatus);
};

export class PhoneNumberAdminService {
  private static async invokePortingAction(action: string, data: Record<string, any>) {
    const { data: result, error } = await supabase.functions.invoke("twilio-handler", {
      body: { action, data },
    });

    if (error) throw new Error(error.message);
    return result;
  }

  /**
   * Get all phone numbers with agent and user details
   */
  static async getAllPhoneNumbers(
    filters?: PhoneNumberFilters,
    limit: number = 50,
    offset: number = 0
  ): Promise<PhoneNumberListResponse> {
    // First, get phone numbers with agent info
    let query = supabase
      .from("agent_phone_numbers")
      .select(
        `
        id,
        phone_number,
        friendly_name,
        status,
        date_created,
        country_code,
        is_transfer,
        metadata,
        agent_id,
        agents!inner (
          id,
          name,
          user_id
        )
      `,
        { count: "exact" }
      )
      .order("date_created", { ascending: false })
      .range(offset, offset + limit - 1);

    // Apply filters
    if (filters?.search) {
      // Search by phone number (strip non-digits for flexible matching)
      const searchDigits = filters.search.replace(/\D/g, "");
      if (searchDigits) {
        query = query.ilike("phone_number", `%${searchDigits}%`);
      }
    }

    if (filters?.country_code) {
      query = query.eq("country_code", filters.country_code);
    }

    if (filters?.status) {
      query = query.eq("status", filters.status);
    }

    if (filters?.is_transfer !== undefined) {
      query = query.eq("is_transfer", filters.is_transfer);
    }

    if (filters?.portingOnly) {
      query = query.eq("metadata->>source", "ported");
    }

    const { data: rawData, error, count } = await query;

    if (error) throw error;

    // Get unique user IDs to fetch profiles
    const userIds = [
      ...new Set(
        (rawData as RawPhoneNumberData[])
          ?.map((item) => item.agents?.user_id)
          .filter((id): id is string => id !== null && id !== undefined)
      ),
    ];

    // Fetch profiles for these users
    let profilesMap: Record<string, { email: string | null; full_name: string | null }> = {};

    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, email, full_name")
        .in("id", userIds);

      if (profiles) {
        profilesMap = profiles.reduce(
          (acc, profile) => {
            acc[profile.id] = { email: profile.email, full_name: profile.full_name };
            return acc;
          },
          {} as Record<string, { email: string | null; full_name: string | null }>
        );
      }
    }

    // Transform data to include user info
    const transformedData: PhoneNumberWithDetails[] = (rawData as RawPhoneNumberData[])?.map((item) => {
      const userId = item.agents?.user_id || null;
      const profile = userId ? profilesMap[userId] : null;
      const portStatus = item.metadata?.port_status || null;

      return {
        id: item.id,
        phone_number: item.phone_number,
        friendly_name: item.friendly_name,
        status: item.status,
        date_created: item.date_created,
        country_code: item.country_code,
        is_transfer: item.is_transfer,
        agent_id: item.agent_id,
        agent_name: item.agents?.name || null,
        user_id: userId,
        user_email: profile?.email || null,
        user_name: profile?.full_name || null,
        metadata: item.metadata,
        is_porting_task: isPortingTask(item),
        port_status: portStatus,
      };
    }) || [];

    return { data: transformedData, count: count || 0 };
  }

  /**
   * Quick search by phone number - returns first match
   */
  static async searchByPhoneNumber(phoneNumber: string): Promise<PhoneNumberWithDetails | null> {
    const searchDigits = phoneNumber.replace(/\D/g, "");
    if (!searchDigits) return null;

    const { data } = await this.getAllPhoneNumbers({ search: searchDigits }, 1, 0);
    return data[0] || null;
  }

  /**
   * Get phone number statistics
   */
  static async getPhoneNumberStats(filters?: PhoneNumberFilters): Promise<PhoneNumberStats> {
    let query = supabase.from("agent_phone_numbers").select("status, country_code, metadata");

    if (filters?.country_code) {
      query = query.eq("country_code", filters.country_code);
    }

    if (filters?.status) {
      query = query.eq("status", filters.status);
    }

    if (filters?.portingOnly) {
      query = query.eq("metadata->>source", "ported");
    }

    const { data, error } = await query;

    if (error) throw error;

    const total = data?.length || 0;
    const active = data?.filter((item) => item.status === "active" || item.status === "in-use").length || 0;
    const portingTasks = data?.filter((item) => isPortingTask(item)).length || 0;
    const portRequested = data?.filter((item) => item.status === "port_requested" || item.metadata?.port_status === "port_requested").length || 0;
    const portPending = data?.filter((item) => item.status === "port_pending" || item.metadata?.port_status === "port_pending").length || 0;

    // Count by country
    const byCountry: Record<string, number> = {};
    data?.forEach((item) => {
      const country = item.country_code || "Unknown";
      byCountry[country] = (byCountry[country] || 0) + 1;
    });

    return {
      total,
      active,
      portingTasks,
      portRequested,
      portPending,
      by_country: byCountry,
    };
  }

  /**
   * Export phone numbers to CSV
   */
  static async exportToCSV(filters?: PhoneNumberFilters): Promise<string> {
    const { data } = await this.getAllPhoneNumbers(filters, 10000, 0);

    const headers = [
      "Phone Number",
      "Friendly Name",
      "Genie Name",
      "User Email",
      "User Name",
      "Country",
      "Status",
      "Type",
      "Date Provisioned",
    ];

    const rows = data.map((item) => [
      item.phone_number,
      item.friendly_name || "",
      item.agent_name || "",
      item.user_email || "",
      item.user_name || "",
      item.country_code || "",
      item.status,
      item.is_transfer ? "Transfer" : "Main",
      new Date(item.date_created).toLocaleDateString(),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    return csvContent;
  }

  /**
   * Get unique country codes for filter dropdown
   */
  static async getCountryCodes(): Promise<string[]> {
    const { data, error } = await supabase
      .from("agent_phone_numbers")
      .select("country_code")
      .not("country_code", "is", null);

    if (error) throw error;

    const uniqueCodes = [...new Set(data?.map((item) => item.country_code).filter(Boolean) as string[])];
    return uniqueCodes.sort();
  }

  static async checkPortability(phoneNumberRecordId: number) {
    return this.invokePortingAction("check-portability", { phoneNumberRecordId });
  }

  static async createTwilioPortInRequest(
    phoneNumberRecordId: number,
    params: {
      documentSid: string;
      targetPortInDate: string;
      targetPortInTimeRangeStart?: string;
      targetPortInTimeRangeEnd?: string;
      customerType: "Business" | "Individual";
      accountTelephoneNumber: string;
      pin?: string;
      address: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        zip: string;
      };
    },
  ) {
    return this.invokePortingAction("create-twilio-port-in-request", { phoneNumberRecordId, ...params });
  }

  static async refreshPortInStatus(phoneNumberRecordId: number) {
    return this.invokePortingAction("refresh-port-in-status", { phoneNumberRecordId });
  }

  static async savePortingNote(
    phoneNumberRecordId: number,
    params: {
      adminNote?: string;
      manualTicketId?: string;
      manualRequestUrl?: string;
      status?: string;
      portStatus?: string;
    },
  ) {
    return this.invokePortingAction("save-porting-note", { phoneNumberRecordId, ...params });
  }

  static async cancelPortingTask(phoneNumberRecordId: number, reason?: string) {
    return this.invokePortingAction("cancel-porting-task", { phoneNumberRecordId, reason });
  }

  static async completeExistingNumberAttachment(phoneNumberRecordId: number) {
    return this.invokePortingAction("complete-existing-number-attachment", { phoneNumberRecordId });
  }
}
